import { useCallback, useEffect, useState } from 'react';
import type { RefObject } from 'react';
import type { PropertiesHyphen } from 'csstype';

export const expandCollapseStates = Object.freeze({
  low: 'collapsed',
  rising: 'expanding',
  high: 'expanded',
  falling: 'collapsing'
} as const);

export const openCloseStates = Object.freeze({
  low: 'closed',
  rising: 'opening',
  high: 'open',
  falling: 'closing'
} as const);

const useTransitionState = <
  Low extends string = 'low',
  Rising extends string = 'rising',
  High extends string = 'high',
  Falling extends string = 'falling'
>({
  ref,
  property,
  states = {
    low: 'low' as Low,
    rising: 'rising' as Rising,
    high: 'high' as High,
    falling: 'falling' as Falling
  },
  defaultState = states.low
}: {
  ref: RefObject<HTMLElement>;
  property: keyof PropertiesHyphen;
} & (
  | {
      states: { low: Low; rising: Rising; high: High; falling: Falling };
      defaultState?: Low | High;
    }
  | {
      states?: { low: 'low'; rising: 'rising'; high: 'high'; falling: 'falling' };
      defaultState?: 'low' | 'high';
    }
)) => {
  const [state, setState] = useState<keyof typeof states>(() =>
    defaultState === states.high ? 'high' : 'low'
  );

  const onTransitionEnd = useCallback(
    (e: TransitionEvent) => {
      if (e.propertyName !== property || e.target !== e.currentTarget) return;
      setState(cur => (cur === 'falling' ? 'low' : 'high'));
    },
    [property]
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.addEventListener('transitionend', onTransitionEnd);
    return () => {
      el.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [onTransitionEnd, ref.current]);

  const set = useCallback((wantsToGo: Low | High) => {
    setState(cur => {
      if (wantsToGo === states.high && cur !== 'high') return 'rising';
      if (wantsToGo === states.low && cur !== 'low') return 'falling';
      return cur;
    });
  }, []);

  const toggle = useCallback(() => {
    setState(cur => {
      if (cur === 'low' || cur === 'falling') return 'rising';
      if (cur === 'high' || cur === 'rising') return 'falling';
      return cur;
    });
  }, []);

  return { state: states[state] as Low | Rising | High | Falling, set, toggle };
};

export default useTransitionState;
